<template>
  <Teleport to="body">
    <div v-if="isOpen" class="fixed inset-0 z-50 overflow-y-auto">
      <div class="flex min-h-full items-center justify-center p-4">
        <!-- Backdrop -->
        <div class="fixed inset-0 bg-gray-500/75 transition-opacity" @click="close" />

        <!-- Modal Panel - Fixed size to prevent resizing -->
        <div class="relative transform overflow-hidden rounded-xl bg-white shadow-2xl sm:w-full flex flex-col" style="width: 640px; height: 580px; max-height: 90vh; max-width: 95vw;">
          <!-- Header (fixed) -->
          <div class="flex-shrink-0 border-b border-gray-100 px-6 py-4">
            <div class="flex items-center justify-between">
              <div class="flex items-center gap-3">
                <div class="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600">
                  <svg class="h-5 w-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <div>
                  <h3 class="text-lg font-semibold text-gray-900">
                    {{ title }}
                  </h3>
                  <p class="text-sm text-gray-500">
                    {{ stepDescription }}
                  </p>
                </div>
              </div>
              <button type="button" @click="close" class="text-gray-400 hover:text-gray-500">
                <svg class="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          <!-- Scrollable Content Area -->
          <div class="flex-1 overflow-y-auto min-h-0">
            <!-- Loading State -->
            <div v-if="step === 'analyzing'" class="px-6 py-4 h-full flex items-center justify-center">
              <div class="text-center">
                <div class="w-16 h-16 mx-auto mb-4 relative">
                  <svg class="w-16 h-16 text-blue-500 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                </div>
                <h4 class="text-lg font-medium text-gray-900 mb-2">
                  Analyzing Images...
                </h4>
                <p class="text-sm text-gray-500">
                  Scanning blocks for missing images
                </p>
              </div>
            </div>

            <!-- Options Form -->
            <div v-else-if="step === 'options'" class="px-6 py-4">
              <div class="space-y-4">
                <!-- Provider Selection -->
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">AI Provider</label>
                  <div class="grid grid-cols-2 gap-3">
                    <button
                      v-for="p in providers"
                      :key="p.value"
                      type="button"
                      @click="selectedProvider = p.value"
                      :class="[
                        'relative flex cursor-pointer items-center rounded-lg border-2 p-4 text-left transition-all',
                        selectedProvider === p.value
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      ]">
                      <div class="flex items-center gap-3">
                        <div :class="['h-10 w-10 rounded-lg flex items-center justify-center', p.bgColor]">
                          <span :class="['text-lg font-semibold', p.textColor]">{{ p.icon }}</span>
                        </div>
                        <div>
                          <p class="text-sm font-medium text-gray-900">
                            {{ p.name }}
                          </p>
                          <p class="text-xs text-gray-500">
                            {{ p.description }}
                          </p>
                        </div>
                      </div>
                      <div
                        v-if="selectedProvider === p.value"
                        class="absolute top-2 right-2 h-5 w-5 rounded-full bg-blue-500 flex items-center justify-center">
                        <svg class="h-3 w-3 text-white" fill="currentColor" viewBox="0 0 12 12">
                          <path d="M3.707 5.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4a1 1 0 00-1.414-1.414L5 6.586 3.707 5.293z" />
                        </svg>
                      </div>
                    </button>
                  </div>
                </div>

                <!-- Style Selection -->
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">Style</label>
                  <select v-model="selectedStyle" class="block w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm">
                    <option v-for="style in styles" :key="style.value" :value="style.value">
                      {{ style.label }}
                    </option>
                  </select>
                </div>

                <!-- Custom Prompt Prefix -->
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">Custom Prompt Prefix (optional)</label>
                  <input
                    v-model="promptPrefix"
                    type="text"
                    placeholder="e.g., Modern, clean, professional..."
                    class="block w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm">
                  <p class="mt-1 text-xs text-gray-500">
                    Added to the beginning of all generated prompts
                  </p>
                </div>
              </div>
            </div>

            <!-- Analysis Results / Preview -->
            <div v-else-if="step === 'preview'" class="px-6 py-4">
              <!-- Injectable Targets (using existing service images) -->
              <div v-if="injectableTargets.length > 0" class="mb-4">
                <div class="flex items-center justify-between mb-2">
                  <div class="min-w-0 flex-1">
                    <h4 class="text-sm font-medium text-green-700">
                      Using Existing Service Images
                    </h4>
                    <p class="text-xs text-green-600">
                      These will use images already uploaded to services
                    </p>
                  </div>
                  <div class="flex-shrink-0 text-right ml-4">
                    <span class="text-xl font-bold text-green-600">{{ injectableTargets.length }}</span>
                  </div>
                </div>
                <div class="space-y-2">
                  <div
                    v-for="(target, idx) in injectableTargets"
                    :key="'inj-' + idx"
                    class="flex items-start gap-3 p-3 bg-green-50 rounded-lg overflow-hidden border border-green-100">
                    <div class="flex-shrink-0 w-8 h-8 rounded-lg bg-green-200 flex items-center justify-center">
                      <svg class="w-4 h-4 text-green-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <div class="flex-1 min-w-0 overflow-hidden">
                      <div class="flex items-center gap-2 mb-1 flex-wrap">
                        <span class="px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-700 whitespace-nowrap">{{ target.block_type }}</span>
                        <span v-if="target.service_name" class="px-2 py-0.5 text-xs font-medium rounded-full bg-green-200 text-green-800 whitespace-nowrap">{{ target.service_name }}</span>
                        <span class="text-xs text-green-600 truncate">{{ target.page_name }}</span>
                      </div>
                      <p class="text-sm text-green-700">
                        Will use existing service image
                      </p>
                    </div>
                    <div v-if="target.existing_image_url" class="flex-shrink-0 w-10 h-10 rounded-lg overflow-hidden border border-green-200">
                      <img :src="target.existing_image_url" alt="" class="w-full h-full object-cover">
                    </div>
                  </div>
                </div>
              </div>

              <!-- Images to Generate -->
              <div v-if="imageTargets.length > 0">
                <div class="flex items-center justify-between mb-2">
                  <div class="min-w-0 flex-1">
                    <h4 class="text-sm font-medium text-gray-900">
                      Images to Generate
                    </h4>
                    <p class="text-xs text-gray-500 truncate">
                      {{ analysisSummary }}
                    </p>
                  </div>
                  <div class="flex-shrink-0 text-right ml-4">
                    <span class="text-2xl font-bold text-blue-600">{{ imageTargets.length }}</span>
                    <span class="text-sm text-gray-500 ml-1">images</span>
                  </div>
                </div>
                <div class="space-y-2">
                  <div
                    v-for="(target, idx) in imageTargets"
                    :key="idx"
                    class="flex items-start gap-3 p-3 bg-gray-50 rounded-lg overflow-hidden">
                    <div class="flex-shrink-0 w-8 h-8 rounded-lg bg-gray-200 flex items-center justify-center">
                      <svg class="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div class="flex-1 min-w-0 overflow-hidden">
                      <div class="flex items-center gap-2 mb-1 flex-wrap">
                        <span class="px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-700 whitespace-nowrap">{{ target.block_type }}</span>
                        <span v-if="target.field_name" class="px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-600 whitespace-nowrap">{{ target.field_name }}</span>
                        <span class="text-xs text-gray-400 truncate">{{ target.page_name }}</span>
                      </div>
                      <p class="text-sm text-gray-600 line-clamp-2 break-words">
                        {{ target.prompt }}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Only injectable, no generation needed -->
              <div v-if="imageTargets.length === 0 && injectableTargets.length > 0" class="mt-4 p-3 bg-green-50 rounded-lg border border-green-100">
                <p class="text-sm text-green-700 text-center">
                  All missing service images can be filled from existing uploads. No AI generation needed!
                </p>
              </div>
            </div>

            <!-- No Images Message -->
            <div v-else-if="step === 'no-images'" class="px-6 py-4 flex items-center justify-center h-full">
              <div class="text-center py-8">
                <div class="w-16 h-16 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center">
                  <svg class="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h4 class="text-lg font-medium text-gray-900 mb-2">
                  All Images Present
                </h4>
                <p class="text-sm text-gray-500">
                  All image fields already have images. No generation needed.
                </p>
              </div>
            </div>

            <!-- Progress Section -->
            <div v-else-if="step === 'progress'" class="px-6 py-4">
              <div class="mb-4">
                <div class="flex items-center justify-between mb-2">
                  <span class="text-sm font-medium text-gray-700">
                    <template v-if="isComplete">
                      <span class="text-green-600 font-medium">
                        <svg class="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                        </svg>
                        Complete!
                      </span>
                    </template>
                    <template v-else>Processing images...</template>
                  </span>
                  <span class="text-sm font-bold text-blue-600">{{ progressPercent }}%</span>
                </div>
                <div class="h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    class="h-full bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full transition-all duration-500"
                    :style="{ width: progressPercent + '%' }" />
                </div>
                <div class="flex justify-between mt-1 text-xs text-gray-500">
                  <span>{{ getProgressStatusText() }}</span>
                  <span v-if="!isComplete && etaText">{{ etaText }}</span>
                </div>
              </div>

              <!-- Injectable Targets Progress -->
              <div v-if="injectableTargets.length > 0" class="mb-3">
                <h5 class="text-xs font-medium text-green-700 mb-2">
                  Existing Service Images
                </h5>
                <div class="space-y-2">
                  <div
                    v-for="(target, idx) in injectableTargets"
                    :key="'inj-prog-' + idx"
                    :class="[
                      'flex items-center gap-3 p-2 rounded-lg transition-all overflow-hidden',
                      target.status === 'completed' ? 'bg-green-50' : target.status === 'failed' ? 'bg-red-50' : 'bg-gray-50'
                    ]">
                    <div :class="['flex-shrink-0 w-6 h-6 rounded flex items-center justify-center', target.status === 'completed' ? 'bg-green-200' : 'bg-gray-200']">
                      <svg v-if="target.status === 'completed'" class="w-3 h-3 text-green-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                      </svg>
                      <svg v-else class="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div class="flex-1 min-w-0">
                      <span class="text-xs text-gray-700">{{ target.service_name }}</span>
                    </div>
                    <div v-if="target.existing_image_url" class="flex-shrink-0 w-6 h-6 rounded overflow-hidden">
                      <img :src="target.existing_image_url" alt="" class="w-full h-full object-cover">
                    </div>
                  </div>
                </div>
              </div>

              <!-- Generated Images Progress -->
              <div v-if="imageTargets.length > 0">
                <h5 v-if="injectableTargets.length > 0" class="text-xs font-medium text-gray-700 mb-2">
                  AI Generated Images
                </h5>
                <div class="space-y-2">
                  <div
                    v-for="(target, idx) in imageTargets"
                    :key="idx"
                    :class="[
                      'flex items-center gap-3 p-3 rounded-lg transition-all overflow-hidden',
                      getItemBgClass(target)
                    ]">
                    <div :class="['flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center', getItemIconBgClass(target)]">
                      <!-- Waiting -->
                      <svg v-if="target.status === 'waiting'" class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <!-- Generating -->
                      <svg v-else-if="target.status === 'generating'" class="w-4 h-4 text-blue-600 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      <!-- Success -->
                      <svg v-else-if="target.status === 'completed'" class="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                      </svg>
                      <!-- Failed -->
                      <svg v-else-if="target.status === 'failed'" class="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </div>
                    <div class="flex-1 min-w-0 overflow-hidden">
                      <div class="flex items-center gap-2 flex-wrap">
                        <span class="text-sm font-medium text-gray-700 whitespace-nowrap">{{ target.block_type }}</span>
                        <span v-if="target.field_name" class="text-xs text-gray-500 whitespace-nowrap">{{ target.field_name }}</span>
                        <span class="text-xs text-gray-400 truncate">{{ target.page_name }}</span>
                      </div>
                      <p :class="['text-xs truncate', getItemStatusClass(target)]">
                        {{ getItemStatusText(target) }}
                      </p>
                    </div>
                    <div v-if="target.image_url" class="flex-shrink-0 w-10 h-10 rounded-lg overflow-hidden">
                      <img :src="target.image_url" alt="" class="w-full h-full object-cover">
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Footer (fixed) -->
          <div class="flex-shrink-0 border-t border-gray-100 px-6 py-4 flex justify-between items-center">
            <div class="text-sm text-gray-500">
              <span v-if="step === 'analyzing'" class="text-blue-600">Scanning blocks...</span>
              <span v-else-if="step === 'preview'" class="text-gray-500">Estimated time: ~{{ estimatedMinutes }} minute{{ estimatedMinutes > 1 ? 's' : '' }}</span>
              <span v-else-if="step === 'progress' && isComplete">
                <span class="text-green-600">
                  {{ injectedCount > 0 ? `${injectedCount} applied` : '' }}{{ injectedCount > 0 && completedCount > 0 ? ', ' : '' }}{{ completedCount > 0 ? `${completedCount} generated` : '' }}
                </span>
                <span v-if="failedCount > 0">, <span class="text-red-600">{{ failedCount }} failed</span></span>
                <span class="text-gray-500 ml-2">• Changes auto-saved</span>
              </span>
              <span v-else-if="errorMessage" class="text-red-600">{{ errorMessage }}</span>
            </div>
            <div class="flex gap-3">
              <!-- Cancel/Close button -->
              <button
                v-if="step !== 'analyzing'"
                type="button"
                @click="close"
                class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">
                {{ isComplete ? 'Close' : 'Cancel' }}
              </button>

              <!-- Options step: Analyze button -->
              <button
                v-if="step === 'options'"
                type="button"
                @click="analyzeImages"
                class="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 inline-flex items-center gap-2">
                <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                Analyze Images
              </button>

              <!-- Preview step: Back and Generate buttons -->
              <template v-else-if="step === 'preview'">
                <button
                  type="button"
                  @click="step = 'options'"
                  class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 inline-flex items-center gap-2">
                  <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
                  </svg>
                  Back
                </button>
                <button
                  type="button"
                  @click="startGeneration"
                  :disabled="isLoading"
                  class="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2">
                  <svg v-if="isLoading" class="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  <svg v-else class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  {{ isLoading ? 'Starting...' : getGenerateButtonText() }}
                </button>
              </template>

              <!-- Progress complete: Done button -->
              <button
                v-else-if="step === 'progress' && isComplete"
                type="button"
                @click="close"
                class="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 inline-flex items-center gap-2">
                Done
              </button>

              <!-- No images: Close button already shown -->
            </div>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { ref, computed, onUnmounted } from 'vue'
import consumer from '~/channels/consumer'

const props = defineProps({
  websiteId: { type: String, required: true },
  pageId: { type: String, default: null },
  scope: { type: String, default: 'page' }
})

const emit = defineEmits(['close'])

// State
const isOpen = ref(false)
const isLoading = ref(false)
const step = ref('options') // options, preview, progress, no-images
const errorMessage = ref('')
const currentJobId = ref(null)
const subscription = ref(null)

// Form state
const selectedProvider = ref('gemini')
const selectedStyle = ref('photorealistic')
const promptPrefix = ref('')

// Analysis results
const imageTargets = ref([])
const injectableTargets = ref([]) // Targets with existing WebsiteService images
const byBlockType = ref({})

// Progress tracking
const completedCount = ref(0)
const failedCount = ref(0)
const injectedCount = ref(0)

// Options
const providers = [
  { value: 'gemini', name: 'Google Imagen 3', description: '~15s per image', icon: 'G', bgColor: 'bg-blue-100', textColor: 'text-blue-600' },
  { value: 'openai', name: 'OpenAI DALL-E 3', description: '~5s per image', icon: 'O', bgColor: 'bg-green-100', textColor: 'text-green-600' }
]

const styles = [
  { value: 'photorealistic', label: 'Photorealistic' },
  { value: 'digital_art', label: 'Digital Art' },
  { value: 'minimalist', label: 'Minimalist' },
  { value: 'watercolor', label: 'Watercolor' },
  { value: '3d_render', label: '3D Render' },
  { value: 'sketch', label: 'Sketch' }
]

// Computed
const title = computed(() => (props.scope === 'page' ? 'Generate Page Images' : 'Generate All Site Images'))

const totalTargetCount = computed(() => imageTargets.value.length + injectableTargets.value.length)

const stepDescription = computed(() => {
  switch (step.value) {
    case 'options':
      return props.scope === 'page' ?
        'Configure options and analyze missing images' :
        'Configure options for site-wide image generation'
    case 'analyzing':
      return 'Scanning for missing images...'
    case 'preview': {
      const parts = []
      if (imageTargets.value.length > 0) parts.push(`${imageTargets.value.length} to generate`)
      if (injectableTargets.value.length > 0) parts.push(`${injectableTargets.value.length} existing`)
      return `Review: ${parts.join(', ')}`
    }
    case 'progress':
      return isComplete.value ?
        'Generation complete' :
        `Processing ${totalTargetCount.value} images...`
    case 'no-images':
      return 'No missing images found'
    default:
      return ''
  }
})

const analysisSummary = computed(() => {
  const entries = Object.entries(byBlockType.value)
  if (entries.length === 0) return `${imageTargets.value.length} images`
  return entries.map(([type, count]) => `${count} ${type}`).join(', ')
})

const progressPercent = computed(() => {
  const total = totalTargetCount.value
  if (total === 0) return 0
  const done = completedCount.value + failedCount.value + injectedCount.value
  return Math.round((done / total) * 100)
})

const isComplete = computed(() => {
  const total = totalTargetCount.value
  if (total === 0) return false
  const done = completedCount.value + failedCount.value + injectedCount.value
  return done >= total
})

const estimatedMinutes = computed(() => {
  const delay = selectedProvider.value === 'gemini' ? 15 : 5
  const totalSeconds = imageTargets.value.length * delay
  return Math.ceil(totalSeconds / 60)
})

const etaText = computed(() => {
  const remaining = imageTargets.value.length - completedCount.value - failedCount.value
  if (remaining <= 0) return ''
  const delay = selectedProvider.value === 'gemini' ? 15 : 5
  const seconds = remaining * delay
  const minutes = Math.floor(seconds / 60)
  const secs = seconds % 60
  return minutes > 0 ? `~${minutes}m ${secs}s remaining` : `~${secs}s remaining`
})

// Methods
function open() {
  isOpen.value = true
  step.value = 'options'
  errorMessage.value = ''
  imageTargets.value = []
  injectableTargets.value = []
  completedCount.value = 0
  failedCount.value = 0
  injectedCount.value = 0
}

function close() {
  // If we completed generation, reload the page to show fresh data
  // This ensures the page editor has the latest block content with generated images
  const shouldReload = step.value === 'progress' && isComplete.value && (completedCount.value > 0 || injectedCount.value > 0)

  isOpen.value = false
  if (subscription.value) {
    subscription.value.unsubscribe()
    subscription.value = null
  }
  emit('close')

  if (shouldReload) {
    // Short delay to let the emit propagate, then reload
    setTimeout(() => {
      window.location.reload()
    }, 100)
  }
}

function reset() {
  step.value = 'options'
  errorMessage.value = ''
  imageTargets.value = []
  injectableTargets.value = []
  byBlockType.value = {}
  completedCount.value = 0
  failedCount.value = 0
  injectedCount.value = 0
  currentJobId.value = null
}

async function analyzeImages() {
  // Immediately switch to analyzing state for smooth transition
  step.value = 'analyzing'
  errorMessage.value = ''

  try {
    const url = getApiUrl()
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-CSRF-Token': csrfToken
      },
      body: JSON.stringify({
        provider: selectedProvider.value,
        style: selectedStyle.value,
        prompt_prefix: promptPrefix.value,
        preview: 'true',
        skip_existing: 'true'
      })
    })

    const data = await response.json()

    if (data.success) {
      currentJobId.value = data.job_id
      imageTargets.value = (data.targets || []).map((t) => ({ ...t, status: 'waiting' }))
      injectableTargets.value = (data.injectable_targets || []).map((t) => ({ ...t, status: 'waiting', injectable: true }))
      byBlockType.value = data.by_block_type || {}

      const totalCount = (data.total || 0) + (data.injectable_count || 0)
      if (totalCount === 0) {
        step.value = 'no-images'
      } else {
        step.value = 'preview'
      }
    } else {
      errorMessage.value = data.error || 'Failed to analyze images'
      step.value = 'options' // Go back to options on error
    }
  } catch (error) {
    console.error('Failed to analyze images:', error)
    errorMessage.value = 'Failed to analyze images. Please try again.'
    step.value = 'options' // Go back to options on error
  }
}

async function startGeneration() {
  if (!currentJobId.value) {
    errorMessage.value = 'No job to start. Please analyze first.'
    return
  }

  isLoading.value = true
  errorMessage.value = ''

  try {
    const url = getStartUrl()
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-CSRF-Token': csrfToken
      },
      body: JSON.stringify({ job_id: currentJobId.value })
    })

    const data = await response.json()

    if (data.success) {
      subscribeToProgress()
      step.value = 'progress'
      // Injectable items are processed immediately by worker,
      // but we'll wait for the broadcast to mark them complete
      // Mark first generation item as generating
      if (imageTargets.value.length > 0) {
        imageTargets.value[0].status = 'generating'
      }
    } else {
      errorMessage.value = data.error || 'Failed to start generation'
    }
  } catch (error) {
    console.error('Failed to start generation:', error)
    errorMessage.value = 'Failed to start generation. Please try again.'
  } finally {
    isLoading.value = false
  }
}

function subscribeToProgress() {
  if (subscription.value) {
    subscription.value.unsubscribe()
  }

  subscription.value = consumer.subscriptions.create(
    { channel: 'WebsiteGenerationChannel', website_id: props.websiteId },
    {
      connected: () => console.log('[BulkImage] Connected to generation channel'),
      disconnected: () => console.log('[BulkImage] Disconnected from generation channel'),
      received: (data) => handleChannelMessage(data)
    }
  )
}

function handleChannelMessage(data) {
  console.log('[BulkImage] Received:', data.event, data)

  if (data.job_id !== currentJobId.value) return

  switch (data.event) {
    case 'bulk_image_job_status':
      handleJobStatus(data)
      break
    case 'bulk_image_item_completed':
      handleItemCompleted(data)
      break
    case 'bulk_image_queued':
      // Queuing complete, first item should start generating
      if (imageTargets.value.length > 0) {
        imageTargets.value[0].status = 'generating'
      }
      break
  }
}

function handleJobStatus(data) {
  completedCount.value = data.completed
  failedCount.value = data.failed
}

function handleItemCompleted(data) {
  // Handle injectable items (existing service images)
  if (data.source === 'existing_service') {
    injectedCount.value++
    const target = injectableTargets.value.find((t) => t.block_id === data.block_id && t.field_name === data.field_name)
    if (target) {
      target.status = data.success ? 'completed' : 'failed'
      if (data.image_url) {
        target.image_url = data.image_url
      }
    }
    return
  }

  // Handle generated images
  completedCount.value = data.completed
  failedCount.value = data.failed

  // Find and update the completed item
  const target = imageTargets.value.find((t) => t.block_id === data.block_id)
  if (target) {
    target.status = data.success ? 'completed' : 'failed'
    if (data.image_url) {
      target.image_url = data.image_url
    }
  }

  // Mark next item as generating
  const nextIndex = data.completed + data.failed
  if (nextIndex < imageTargets.value.length) {
    imageTargets.value[nextIndex].status = 'generating'
  }
}

function getApiUrl() {
  if (props.scope === 'site') {
    return `/admin/websites/${props.websiteId}/editor/bulk_generate_all_images`
  }
  return `/admin/websites/${props.websiteId}/pages/${props.pageId}/bulk_generate_images`
}

function getStartUrl() {
  if (props.scope === 'site') {
    return `/admin/websites/${props.websiteId}/editor/start_bulk_generation`
  }
  return `/admin/websites/${props.websiteId}/pages/${props.pageId}/bulk_generate_images/start`
}

function getItemBgClass(target) {
  switch (target.status) {
    case 'generating': return 'bg-blue-50'
    case 'completed': return 'bg-green-50'
    case 'failed': return 'bg-red-50'
    default: return 'bg-gray-50'
  }
}

function getItemIconBgClass(target) {
  switch (target.status) {
    case 'generating': return 'bg-blue-100'
    case 'completed': return 'bg-green-100'
    case 'failed': return 'bg-red-100'
    default: return 'bg-gray-200'
  }
}

function getItemStatusClass(target) {
  switch (target.status) {
    case 'generating': return 'text-blue-600'
    case 'completed': return 'text-green-600'
    case 'failed': return 'text-red-600'
    default: return 'text-gray-500'
  }
}

function getItemStatusText(target) {
  if (target.injectable) {
    switch (target.status) {
      case 'completed': return 'Applied existing service image'
      case 'failed': return 'Failed to apply'
      default: return 'Will use existing image'
    }
  }
  switch (target.status) {
    case 'generating': return 'Generating...'
    case 'completed': return 'Generated successfully'
    case 'failed': return 'Failed to generate'
    default: return 'Waiting...'
  }
}

function getGenerateButtonText() {
  if (imageTargets.value.length === 0 && injectableTargets.value.length > 0) {
    return `Apply ${injectableTargets.value.length} Service Images`
  }
  if (injectableTargets.value.length > 0) {
    return `Process ${totalTargetCount.value} Images`
  }
  return `Generate ${imageTargets.value.length} Images`
}

function getProgressStatusText() {
  const parts = []
  const done = completedCount.value + failedCount.value + injectedCount.value
  const total = totalTargetCount.value

  if (injectedCount.value > 0) {
    parts.push(`${injectedCount.value} applied`)
  }
  if (completedCount.value > 0) {
    parts.push(`${completedCount.value} generated`)
  }
  if (failedCount.value > 0) {
    parts.push(`${failedCount.value} failed`)
  }

  if (parts.length === 0) {
    return `0 of ${total} completed`
  }

  return `${done} of ${total}: ${parts.join(', ')}`
}

// Cleanup
onUnmounted(() => {
  if (subscription.value) {
    subscription.value.unsubscribe()
  }
})

// Expose open method for parent components
defineExpose({ open, close, reset })
</script>
